// @ts-check
import { EChart, LegendIcon } from '/b2b/common/components/Charts';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import {
    axisNameForTimeFormat,
    getTimeFormat,
    maxTimeInterval,
    timeLabelFormatter,
} from '/b2b/common/components/Charts/helpers';

import { CMP_CONSENT_PLATFORMS } from '/b2b/consent/constants';
import ConsentDashboardContext from '../ConsentDashboard.context';
import PropTypes from 'prop-types';
import WidgetContainer from '/b2b/common/components/Reporting/WidgetContainer';
import { escapeCSVValue } from '/b2b/common/helpers/CSV';
import { fetchConsentReportsDashboard } from '/b2b/consent/services';
import { formatChartTooltip } from '/b2b/consent/format';
import { makeDownloadBlob } from '/b2b/common/components/Reporting/helpers';
import merge from 'lodash/merge';
import moment from 'moment';
import { parseDate } from '/b2b/common/helpers/Date';
import { translate } from '/b2b/common/helpers/i18n';
import useBoundCallback from '/b2b/common/helpers/Hooks/useBoundCallback';
import { useTheme } from '@mui/material/styles';
import { withAsyncData } from '/b2b/common/hoc/withAsyncData';
import withRetranslate from '/b2b/common/hoc/withRetranslate';

export const REPORT_NAME = 'consentsByPlatform';

const commonSeries = {
    type: 'bar',
    stack: 'total',
    barMinHeight: 1,
    label: {
        show: false,
    },
    emphasis: {
        focus: 'series',
    },
};

/** @type {import('echarts').EChartsOption} */
const staticOptions = {
    legend: {
        icon: LegendIcon,
        orient: 'horizontal',
        top: 'top',
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'none',
        },
        appendTo: document.body,
    },
    grid: {
        containLabel: false,
    },
    useUTC: true,
    xAxis: {
        axisLabel: {
            hideOverlap: true,
            showMaxLabel: false,
            showMinLabel: false,
        },
        axisLine: { onZero: false, show: true },
        axisTick: { show: false },
        minorTick: { show: false },
        nameGap: 32,
        nameLocation: 'middle',
        nameTextStyle: {
            verticalAlign: 'top',
        },
        splitLine: { show: false },
        type: 'time',
    },
    yAxis: {
        axisLabel: { align: 'right', margin: 0, showMinLabel: false },
        axisLine: { show: false },
        axisTick: { show: false },
        nameLocation: 'middle',
        nameRotate: 90,
        nameTextStyle: {
            verticalAlign: 'bottom',
        },
        splitLine: { show: true },
        splitNumber: 2,
        type: 'value',
    },
    // @ts-ignore
    series: Object.values(CMP_CONSENT_PLATFORMS).map(platform => ({
        ...commonSeries,
        name: `common.${platform}`,
        dimensions: [
            { name: 'date', type: 'ordinal' },
            { name: `${platform}TotalConsents`, type: 'int' },
        ],
    })),
};

const csvColumnMap = {
    date: 'common.date',
    .../** @type {{[K in CmpConsentPlatformsValues as `${K}TotalConsents`]: string;}} */ (
        Object.values(CMP_CONSENT_PLATFORMS).reduce((acc, platform) => {
            acc[`${platform}TotalConsents`] = ['common.total', `common.${platform}`];
            return acc;
        }, {})
    ),
};

/**
 * @param {Unpacked<CmpReportsData['consentsByPlatform']>} row
 * @param {Array<keyof typeof csvColumnMap>} columnNames
 * @return {(string|number)[]}
 */
const processRow = (row, columnNames) => {
    return columnNames
        .reduce((acc, column) => {
            switch (column) {
                case 'date':
                    acc.push(moment(parseDate(row?.[column])).format('YYYY-MM-DD'));
                    break;
                default:
                    acc.push(row?.[column] ?? 0);
                    break;
            }
            return acc;
        }, /** @type {(string|number)[]} */ ([]))
        .filter(val => val !== undefined);
};

/**
 * @param {CmpReportsData} data
 * @returns {Blob}
 */
function makeCSV(data) {
    const { consentsByPlatform = [] } = data || { consentsByPlatform: undefined };
    const format = getTimeFormat(data.dateRange[0], data.dateRange[1]);
    const columnNames = /** @type {(keyof csvColumnMap)[]} */ (Object.keys(csvColumnMap));
    // String CSV
    const csvFile = consentsByPlatform
        .reduce(
            (acc, row) => {
                const rowArray = processRow(row, columnNames);
                acc.push(rowArray.map(escapeCSVValue).join(','));
                return acc;
            },
            [
                columnNames
                    .map(column => {
                        switch (column) {
                            case 'date':
                                return translate(
                                    axisNameForTimeFormat(format) || csvColumnMap[column],
                                    { date: '' }
                                );
                            default:
                                return translate(csvColumnMap[column], { delimiter: ' - ' });
                        }
                    })
                    .map(escapeCSVValue)
                    .join(','),
            ]
        )
        .join('\n');
    return new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
}

export const ConsentsByPlatform = props => {
    const { data: providedData, filters, loading: providedLoading, shouldRetranslate } = props;
    const { data, error, load, loading } = useContext(ConsentDashboardContext);
    /** @type {import('/b2b/common/theme').OsanoDefaultTheme} */
    const theme = useTheme();
    const { consentsByPlatform = [], dateRange = [], totalRange = [] } = data || {};
    const noResults =
        consentsByPlatform.length === 0 ||
        !consentsByPlatform.some(({ totalConsents }) => totalConsents > 0);
    const isLoading = loading || providedLoading;

    const echartsInstance = useRef(null);
    const onChartReady = useCallback(chart => {
        echartsInstance.current = chart;
    }, []);

    const memoOption = useMemo(() => {
        const minTotal = totalRange[0] || 0;
        let scale = 1;
        if (minTotal >= 1000) {
            scale = 1000;
        } else if (minTotal >= 1000000) {
            scale = 1000000;
        }

        const dates = consentsByPlatform.map(({ date }) =>
            moment(parseDate(date)).format('YYYY-MM-DD')
        );
        const format = getTimeFormat(dateRange[0], dateRange[1]);
        return merge(
            {},
            staticOptions,
            /** @type {import('echarts').EChartsOption} */ ({
                legend: {
                    left: theme.spacing(1.5),
                },
                tooltip: {
                    formatter: formatChartTooltip(format),
                },
                grid: {
                    left: theme.spacing(8.5),
                    right: theme.spacing(4.5),
                },
                xAxis: {
                    name: translate(axisNameForTimeFormat(format), { date: '' }).trim(),
                    axisLabel: {
                        formatter: timeLabelFormatter(dateRange[0], dateRange[1]),
                        customValues: dates,
                    },
                    max: dateRange[1],
                    min: dateRange[0],
                    maxInterval: maxTimeInterval(dateRange[0], dateRange[1]),
                },
                yAxis: {
                    show: consentsByPlatform.length > 0,
                    name: translate('common.consents'),
                    nameGap: parseInt(theme.spacing(5), 10),
                    axisLabel: {
                        formatter: value => {
                            switch (scale) {
                                case 1000:
                                    return `${value / scale}k`;
                                case 1000000:
                                    return `${value / scale}m`;
                                default:
                                    return value;
                            }
                        },
                    },
                },
                dataset: {
                    source: consentsByPlatform,
                },
                // @ts-ignore
                series: staticOptions.series.map(series => {
                    return {
                        ...series,
                        name: translate(series.name),
                    };
                }),
            })
        );
    }, [consentsByPlatform, shouldRetranslate]);

    const fetchData = useBoundCallback(
        filters =>
            fetchConsentReportsDashboard({
                filters: {
                    ...filters,
                    requestedCharts: [REPORT_NAME],
                },
            }),
        [filters]
    );

    const helpers = useMemo(
        () => ({
            makeCSV,
            name: 'consents-by-platform',
        }),
        []
    );
    const makeDownload = useBoundCallback(makeDownloadBlob, [echartsInstance, data, helpers]);

    useLayoutEffect(() => {
        providedData ? load(Promise.resolve(providedData)) : load(fetchData());
    }, [providedData]);

    return (
        <WidgetContainer
            disabled={noResults}
            downloadFormats={['csv', 'png']}
            echartsInstance={echartsInstance}
            error={error}
            title="consent.dashboard.charts.title.consentsByPlatform"
            tooltip="consent.dashboard.charts.tooltip.consentsByPlatform"
        >
            <EChart
                onChartReady={onChartReady}
                option={memoOption}
                loading={isLoading}
                noResults={noResults}
                makeDownload={makeDownload}
            />
        </WidgetContainer>
    );
};

ConsentsByPlatform.propTypes = {
    data: PropTypes.object,
    filters: PropTypes.object,
    loading: PropTypes.bool,
    shouldRetranslate: PropTypes.string,
};

/** @type {import('/b2b/common/hoc/withAsyncData').ComponentWithOverriddenProps<CmpReportsData, { filters: CmpReportsQueryParams }>} */
const TranslatedComponent = withRetranslate(ConsentsByPlatform);

export default withAsyncData(TranslatedComponent, ConsentDashboardContext);
